import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock3, GripHorizontal, MapPin, Radio, Search, X } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import socketService from '@/services/socketService';
import {
  readinessLocation,
  readinessRank,
  readinessStatusMeta,
  usableReadinessDistance,
} from './personnelAvailability';
import { mergeIncidentReadinessRows } from './liveIncidentReadiness';
import PersonnelAvailabilitySummary from './PersonnelAvailabilitySummary';

const TONE_CLASS = {
  ready: 'border-emerald-700/70 bg-emerald-950/70 text-emerald-300',
  danger: 'border-rose-700/70 bg-rose-950/70 text-rose-300',
  pending: 'border-amber-700/70 bg-amber-950/70 text-amber-300',
  muted: 'border-slate-600 bg-slate-800 text-slate-300',
};

const SOURCE_META = {
  department: { label: 'Department', tone: 'border-blue-800/80 bg-blue-950/50 text-blue-200' },
  team: { label: 'Team', tone: 'border-violet-800/80 bg-violet-950/50 text-violet-200' },
  crew: { label: 'Crew', tone: 'border-cyan-800/80 bg-cyan-950/50 text-cyan-200' },
  user: { label: 'Direct', tone: 'border-slate-600 bg-slate-800 text-slate-300' },
};

const EMPTY_COUNTS = {
  ready: 0,
  pending: 0,
  unavailable: 0,
  noResponse: 0,
};

export const summarizeIncidentReadiness = (rows = []) => rows.reduce((counts, row) => {
  const status = String(row?.status || '').toLowerCase();
  if (status === 'in_duty') counts.ready += 1;
  else if (status === 'not_in_duty') counts.unavailable += 1;
  else if (status === 'no_response') counts.noResponse += 1;
  else counts.pending += 1;
  return counts;
}, { ...EMPTY_COUNTS });

export const incidentReadinessPercentage = (counts = EMPTY_COUNTS) => {
  const safeCount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };
  const ready = safeCount(counts?.ready);
  const total = ready
    + safeCount(counts?.pending)
    + safeCount(counts?.unavailable)
    + safeCount(counts?.noResponse);
  if (!total) return 0;
  return Math.min(100, Math.max(0, Math.round((ready / total) * 100)));
};

export const sortIncidentReadinessRows = (rows = []) => [...rows].sort((left, right) => (
  readinessRank(left, 'loaded') - readinessRank(right, 'loaded')
  || usableReadinessDistance(left) - usableReadinessDistance(right)
  || String(left?.user_name || '').localeCompare(String(right?.user_name || ''))
));

export const incidentReadinessCandidateSources = (row = {}) => {
  const exactSources = Array.isArray(row?.candidate_sources) ? row.candidate_sources : [];
  const normalizedExact = exactSources.reduce((sources, source) => {
    const type = String(source?.type || '').trim().toLowerCase();
    const id = String(source?.id || '').trim();
    const name = String(source?.name || id).trim();
    if (!SOURCE_META[type] || !id || !name) return sources;
    const key = `${type}:${id}`;
    if (!sources.some((item) => item.key === key)) {
      sources.push({ key, type, id, name, exact: true });
    }
    return sources;
  }, []);
  return normalizedExact;
};

export const visibleIncidentReadinessCandidateSources = (row = {}) => (
  incidentReadinessCandidateSources(row).filter((source) => source.type !== 'user')
);

export const filterIncidentReadinessRows = (rows = [], query = '') => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return rows;
  return rows.filter((row) => [
    row?.user_name,
    row?.email,
    row?.user_email,
    ...(Array.isArray(row?.proposed_roles) ? row.proposed_roles : []),
    ...incidentReadinessCandidateSources(row).flatMap((source) => [
      source.name,
      SOURCE_META[source.type]?.label,
      source.type === 'user' ? 'direct selection' : '',
    ]),
  ].some((value) => String(value || '').toLowerCase().includes(normalizedQuery)));
};

export const filterIncidentReadinessByStatus = (rows = [], filter = 'all') => {
  if (!filter || filter === 'all') return rows;
  return rows.filter((row) => {
    const status = String(row?.status || '').toLowerCase();
    if (filter === 'ready') return status === 'in_duty';
    if (filter === 'unavailable') return status === 'not_in_duty';
    if (filter === 'noResponse') return status === 'no_response';
    return status !== 'in_duty' && status !== 'not_in_duty' && status !== 'no_response';
  });
};

export const constrainPanelPosition = (position, panelSize, viewportSize, margin = 8) => {
  const width = Math.max(0, Number(panelSize?.width) || 0);
  const height = Math.max(0, Number(panelSize?.height) || 0);
  const viewportWidth = Math.max(0, Number(viewportSize?.width) || 0);
  const viewportHeight = Math.max(0, Number(viewportSize?.height) || 0);
  const safeMargin = Math.max(0, Number(margin) || 0);
  const maxX = Math.max(safeMargin, viewportWidth - width - safeMargin);
  const maxY = Math.max(safeMargin, viewportHeight - height - safeMargin);
  return {
    x: Math.min(maxX, Math.max(safeMargin, Number(position?.x) || safeMargin)),
    y: Math.min(maxY, Math.max(safeMargin, Number(position?.y) || safeMargin)),
  };
};

const formatCheckTiming = (check) => {
  if (!check?.id) return 'No incident check sent';
  if (String(check.status || '').toLowerCase() === 'expired') return 'Expired';
  const expiresAt = new Date(check.expires_at);
  if (Number.isNaN(expiresAt.getTime())) return 'Active';
  return `Active until ${expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const responseErrorMessage = (response, fallback) => response?.message || response?.detail || fallback;
const keepPreviousWhenEqual = (previous, next) => (
  JSON.stringify(previous) === JSON.stringify(next) ? previous : next
);

export default function IncidentReadinessPanel({ incident, sendDisabled = false, onlineUserIds = new Set(), presenceByUserId = {} }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expiry, setExpiry] = useState(5);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [check, setCheck] = useState(null);
  const [rows, setRows] = useState([]);
  const [responseStats, setResponseStats] = useState(null);
  const [notice, setNotice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [panelPosition, setPanelPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const requestSequence = useRef(0);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const rowsRef = useRef(rows);
  const locationRefreshTimerRef = useRef(null);

  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!incident?.id) return;
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    if (!silent) setLoading(true);
    try {
      const latest = await CommanServices.getIncidentReadinessCheck(incident.id);
      if (latest?.status === false) throw new Error(responseErrorMessage(latest, 'Unable to load readiness'));
      const current = latest?.data || null;
      let nextRows = [];
      let nextStats = null;
      if (current?.id) {
        const response = await CommanServices.getDutyCheckResponses(current.id);
        if (response?.status === false) throw new Error(responseErrorMessage(response, 'Unable to load readiness responses'));
        nextStats = response?.data?.stats || null;
        nextRows = (response?.data?.rows || []).filter((row) => (
          row?.user_id && String(row?.user_name || '').trim().toLowerCase() !== 'unknown'
        ));
      }
      if (sequence !== requestSequence.current) return;
      setCheck((previous) => keepPreviousWhenEqual(previous, current));
      setRows((previous) => keepPreviousWhenEqual(previous, nextRows));
      setResponseStats((previous) => keepPreviousWhenEqual(previous, nextStats));
      setNotice((currentNotice) => currentNotice?.tone === 'error' ? null : currentNotice);
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setNotice({ tone: 'error', text: error?.message || 'Unable to load incident readiness' });
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [incident?.id]);

  useEffect(() => {
    requestSequence.current += 1;
    setCheck(null);
    setRows([]);
    setResponseStats(null);
    setNotice(null);
    setSearchQuery('');
    setStatusFilter('all');
    setLoading(true);
    load();
  }, [incident?.id, load]);

  useEffect(() => {
    if (!incident?.id) return undefined;
    const unsubscribe = socketService.on('staffing_readiness_updated', (event) => {
      if (String(event?.incident_id) !== String(incident.id)) return;
      if (event?.user_id) {
        setRows((previous) => mergeIncidentReadinessRows(previous, event));
        setResponseStats(null);
      } else {
        load({ silent: true });
      }
    });
    const unsubscribeReconnect = socketService.on('connect', () => load({ silent: true }));
    return () => {
      unsubscribe?.();
      unsubscribeReconnect?.();
    };
  }, [incident?.id, load]);

  useEffect(() => {
    if (!isOpen || !incident?.id) return undefined;
    const refreshForLocation = (event) => {
      const userId = String(event?.user_id || '');
      if (!userId || !(rowsRef.current || []).some((row) => String(row?.user_id || '') === userId)) return;
      if (locationRefreshTimerRef.current) window.clearTimeout(locationRefreshTimerRef.current);
      locationRefreshTimerRef.current = window.setTimeout(() => {
        locationRefreshTimerRef.current = null;
        load({ silent: true });
      }, 1000);
    };
    const unsubscribeLocation = socketService.on('team_location_updated', refreshForLocation);
    const unsubscribeOffline = socketService.on('team_location_offline', refreshForLocation);
    return () => {
      unsubscribeLocation?.();
      unsubscribeOffline?.();
      if (locationRefreshTimerRef.current) window.clearTimeout(locationRefreshTimerRef.current);
      locationRefreshTimerRef.current = null;
    };
  }, [incident?.id, isOpen, load]);

  useEffect(() => {
    if (!check?.id || String(check.status || '').toLowerCase() === 'expired') return undefined;
    const expiresAt = new Date(check.expires_at).getTime();
    if (!Number.isFinite(expiresAt)) return undefined;
    const delay = Math.min(Math.max(0, expiresAt - Date.now() + 750), 2147483647);
    const timer = window.setTimeout(() => load({ silent: true }), delay);
    return () => window.clearTimeout(timer);
  }, [check?.expires_at, check?.id, check?.status, load]);

  useEffect(() => {
    if (!check?.id || String(check.status || '').toLowerCase() === 'expired') return undefined;
    const interval = window.setInterval(() => load({ silent: true }), 15000);
    return () => window.clearInterval(interval);
  }, [check?.id, check?.status, load]);

  const visibleRows = useMemo(() => sortIncidentReadinessRows(
    filterIncidentReadinessRows(filterIncidentReadinessByStatus(rows, statusFilter), searchQuery),
  ), [rows, searchQuery, statusFilter]);
  const counts = useMemo(() => {
    if (!responseStats || !Number.isFinite(Number(responseStats.total))) return summarizeIncidentReadiness(rows);
    return {
      ready: Math.max(0, Number(responseStats.in_duty) || 0),
      pending: Math.max(0, Number(responseStats.pending) || 0),
      unavailable: Math.max(0, Number(responseStats.not_in_duty) || 0),
      noResponse: Math.max(0, Number(responseStats.no_response) || 0),
    };
  }, [responseStats, rows]);
  const readinessPercentage = useMemo(() => incidentReadinessPercentage(counts), [counts]);
  const readinessRecipientCount = counts.ready + counts.pending + counts.unavailable + counts.noResponse;
  const checkIsActive = !!check?.id && String(check.status || '').toLowerCase() !== 'expired';
  const statusTone = checkIsActive && counts.pending > 0
    ? 'pending'
    : counts.unavailable > 0
      ? 'danger'
      : check?.id && counts.ready > 0
        ? 'ready'
        : 'muted';

  const send = async () => {
    if (sending || sendDisabled || !incident?.id) return;
    setSending(true);
    setNotice(null);
    try {
      const response = await CommanServices.sendIncidentReadinessCheck(incident.id, expiry);
      if (response?.status === false) throw new Error(responseErrorMessage(response, 'Unable to send readiness check'));
      setNotice({
        tone: 'success',
        text: `Readiness check sent to ${response?.data?.target_count || 0} ${Number(response?.data?.target_count || 0) === 1 ? 'person' : 'people'}.`,
      });
      setStatusFilter('all');
      await load({ silent: true });
    } catch (error) {
      setNotice({ tone: 'error', text: error?.message || 'Unable to send readiness check' });
    } finally {
      setSending(false);
    }
  };

  const clampPanelToViewport = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    setPanelPosition((current) => current ? constrainPanelPosition(
      current,
      { width: rect.width, height: rect.height },
      { width: window.innerWidth, height: window.innerHeight },
    ) : current);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('resize', clampPanelToViewport);
    const observer = typeof ResizeObserver !== 'undefined' && panelRef.current
      ? new ResizeObserver(clampPanelToViewport)
      : null;
    if (observer && panelRef.current) observer.observe(panelRef.current);
    return () => {
      window.removeEventListener('resize', clampPanelToViewport);
      observer?.disconnect();
    };
  }, [clampPanelToViewport, isOpen]);

  const startPanelDrag = (event) => {
    if ((event.pointerType === 'mouse' && event.button !== 0) || event.target.closest('button, a, input, select, textarea')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    setPanelPosition({ x: rect.left, y: rect.top });
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const movePanel = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPanelPosition(constrainPanelPosition(
      { x: event.clientX - drag.offsetX, y: event.clientY - drag.offsetY },
      { width: drag.width, height: drag.height },
      { width: window.innerWidth, height: window.innerHeight },
    ));
    event.preventDefault();
  };

  const finishPanelDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return createPortal(
    <>
      {isOpen && (
        <aside
          id="incident-readiness-panel"
          role="complementary"
          aria-label="Incident readiness"
          aria-live="polite"
          ref={panelRef}
          style={panelPosition ? { left: panelPosition.x, top: panelPosition.y, right: 'auto', bottom: 'auto' } : undefined}
          className={`fixed bottom-24 right-4 z-[910] flex max-h-[72vh] w-[calc(100vw-2rem)] origin-bottom-right animate-in flex-col overflow-hidden rounded-2xl border border-cyan-800/70 bg-slate-950 text-left shadow-2xl shadow-black/60 zoom-in-95 slide-in-from-bottom-4 duration-200 sm:right-5 sm:w-[420px] ${isDragging ? 'ring-2 ring-cyan-400/70' : ''}`}
          data-testid="incident-readiness-panel"
        >
          <div
            className={`relative flex touch-none select-none items-start justify-between gap-3 border-b border-slate-700/80 bg-gradient-to-br from-cyan-950 via-slate-900 to-slate-950 px-4 py-4 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onPointerDown={startPanelDrag}
            onPointerMove={movePanel}
            onPointerUp={finishPanelDrag}
            onPointerCancel={finishPanelDrag}
            title="Drag to move the readiness panel"
            data-testid="incident-readiness-drag-handle"
          >
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
            <GripHorizontal className="pointer-events-none absolute left-1/2 top-1 h-3.5 w-3.5 -translate-x-1/2 text-slate-600" aria-hidden="true" />
            <div className="flex min-w-0 items-start gap-3">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-cyan-700/70 bg-cyan-950 text-cyan-300 shadow-inner">
                <Radio className="h-5 w-5" />
                {checkIsActive && <span className="absolute -right-1 -top-1 h-3 w-3 animate-pulse rounded-full border-2 border-slate-950 bg-emerald-400" />}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-extrabold uppercase tracking-[0.14em] text-white">Incident readiness</h3>
                  {checkIsActive && <span className="rounded-full border border-emerald-700/70 bg-emerald-950/70 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-emerald-300">Live</span>}
                </div>
                <p className="mt-1 truncate text-[11px] text-slate-400">Check proposed people for {incident?.incident_id || incident?.id}.</p>
              </div>
            </div>
            <button type="button" onClick={() => setIsOpen(false)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-800 hover:text-white" aria-label="Collapse incident readiness"><X className="h-4 w-4" /></button>
          </div>

          <div className="no-scrollbar flex-1 overflow-y-auto p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${TONE_CLASS[statusTone]}`}>
                <Clock3 className="h-3.5 w-3.5" /> {loading && !check?.id ? 'Loading…' : formatCheckTiming(check)}
              </span>
              {check?.id && <span className="text-[10px] text-slate-500">Updates appear automatically</span>}
            </div>

            {check?.id && (
              <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid="incident-readiness-counts">
                {[
                  ['Ready', counts.ready, 'text-emerald-300', 'ready'],
                  ['Pending', counts.pending, 'text-amber-300', 'pending'],
                  ['Unavailable', counts.unavailable, 'text-rose-300', 'unavailable'],
                  ['No response', counts.noResponse, 'text-slate-200', 'noResponse'],
                ].map(([label, value, tone, filterKey]) => (
                  <button
                    type="button"
                    key={label}
                    onClick={() => setStatusFilter((current) => current === filterKey ? 'all' : filterKey)}
                    className={`rounded-lg border px-1 py-2 text-center transition ${statusFilter === filterKey ? 'border-cyan-400 bg-cyan-950/60 ring-1 ring-cyan-400/40' : 'border-slate-700/80 bg-slate-900/80 hover:border-slate-500 hover:bg-slate-800'}`}
                    aria-pressed={statusFilter === filterKey}
                    aria-label={`${statusFilter === filterKey ? 'Clear' : 'Filter by'} ${label.toLowerCase()} readiness responses: ${value}`}
                    data-testid={`readiness-filter-${filterKey}`}
                  >
                    <p className={`text-sm font-black ${tone}`}>{value}</p>
                    <p className="mt-0.5 text-[8px] font-bold uppercase leading-tight tracking-wide text-slate-500">{label}</p>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-3 rounded-xl border border-cyan-900/80 bg-gradient-to-br from-cyan-950/50 via-slate-900/70 to-slate-900 p-2.5">
              <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2.5">
                <span className="inline-flex h-9 shrink-0 items-center whitespace-nowrap border-r border-slate-700 pr-2.5 text-[8px] font-bold uppercase tracking-wider text-slate-500">Response window</span>
                <div className="grid h-9 min-w-0 grid-cols-3 rounded-lg border border-slate-700 bg-slate-950/80 p-1" role="group" aria-label="Readiness response window">
                  {[2, 5, 10].map((minutes) => (
                    <button
                      type="button"
                      key={minutes}
                      onClick={() => setExpiry(minutes)}
                      disabled={sendDisabled || sending}
                      className={`inline-flex h-7 items-center justify-center rounded-md px-2 text-[10px] font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${expiry === minutes ? 'bg-cyan-600 text-white shadow' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                      aria-pressed={expiry === minutes}
                    >
                      {minutes}m
                    </button>
                  ))}
                </div>
                <button type="button" disabled={sendDisabled || sending || loading} onClick={send} className="inline-flex h-9 min-w-[76px] items-center justify-center whitespace-nowrap rounded-lg bg-cyan-600 px-3 text-[11px] font-extrabold text-white shadow-lg shadow-cyan-950/40 transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50" data-testid="send-incident-readiness">
                  {sending ? 'Sending…' : 'Send check'}
                </button>
              </div>
              {sendDisabled && <p className="mt-2 text-[10px] text-amber-300">This incident is closed. Previous readiness results remain available to review.</p>}
            </div>

            {notice && <p className={`mt-2 rounded-lg border px-2.5 py-2 text-[10px] ${notice.tone === 'error' ? 'border-rose-800 bg-rose-950/40 text-rose-300' : 'border-emerald-800 bg-emerald-950/30 text-emerald-300'}`}>{notice.text}</p>}

            {check?.id ? (
              <div className="mt-4 space-y-2" data-testid="incident-readiness-people">
                <div className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/80 px-2.5 py-2 focus-within:border-cyan-600">
                  <Search className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search people, roles, or groups"
                    className="min-w-0 flex-1 bg-transparent text-[11px] text-white outline-none placeholder:text-slate-600"
                    aria-label="Search readiness people, roles, departments, teams, or crews"
                    data-testid="incident-readiness-search"
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery('')} className="rounded p-0.5 text-slate-500 hover:bg-slate-800 hover:text-white" aria-label="Clear readiness search"><X className="h-3 w-3" /></button>
                  )}
                  <span className="shrink-0 text-[9px] font-semibold text-slate-500">{visibleRows.length}/{rows.length}</span>
                </div>
                {visibleRows.map((row) => {
                  const status = readinessStatusMeta(row, 'loaded');
                  const location = readinessLocation(row);
                  const candidateSources = visibleIncidentReadinessCandidateSources(row);
                  const presence = presenceByUserId?.[String(row.user_id)]
                    || (onlineUserIds.has(String(row.user_id)) ? { online: true } : null);
                  return (
                    <div key={row.user_id} className="rounded-xl border border-slate-700/80 bg-slate-900/70 px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-white">{row.user_name}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold ${TONE_CLASS[status.tone] || TONE_CLASS.muted}`}>{status.label}</span>
                        <PersonnelAvailabilitySummary
                          presence={presence}
                          showDuty={false}
                          showIncident={false}
                          showLocation={false}
                          variant="dark"
                          align="start"
                          testId={`readiness-person-presence-${row.user_id}`}
                        />
                        {(row.proposed_roles || []).length > 0 && <span className="ml-auto truncate text-[10px] font-semibold text-violet-300" title={row.proposed_roles.join(', ')}>{row.proposed_roles.join(', ')}</span>}
                      </div>
                      {candidateSources.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1" aria-label={`Scenario candidate sources for ${row.user_name}`}>
                          {candidateSources.map((source) => {
                            const sourceMeta = SOURCE_META[source.type];
                            return (
                              <span
                                key={source.key}
                                className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] ${sourceMeta.tone}`}
                                title="Selected as a candidate in Scenario Builder"
                              >
                                <span className="font-extrabold uppercase tracking-wide">{sourceMeta.label}</span>
                                <span className="truncate font-semibold">{source.name}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className={`mt-1.5 inline-flex items-center gap-1 text-[10px] ${location.stale ? 'font-semibold text-amber-300' : 'text-slate-400'}`} title={location.stale ? 'Outdated GPS is excluded from ETA and ranking.' : undefined}><MapPin className="h-3 w-3" /> {location.text}</p>
                    </div>
                  );
                })}
                {!loading && !visibleRows.length && (
                  <p className="py-4 text-center text-xs text-slate-500">
                    {rows.length
                      ? searchQuery.trim()
                        ? `No people, roles, or groups match “${searchQuery.trim()}” in this status.`
                        : `No ${statusFilter === 'noResponse' ? 'no response' : statusFilter} readiness responses.`
                      : 'No readiness recipients were found for this check.'}
                  </p>
                )}
              </div>
            ) : (
              <div className="py-7 text-center">
                <Radio className="mx-auto h-7 w-7 text-slate-700" />
                <p className="mt-2 text-xs font-semibold text-slate-400">No incident readiness check yet</p>
                <p className="mt-1 text-[10px] text-slate-600">Send one here, then keep working while responses arrive.</p>
              </div>
            )}
          </div>
        </aside>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className={`group fixed bottom-5 right-5 z-[900] flex h-16 w-16 items-center justify-center rounded-full border text-white shadow-2xl transition-all duration-200 hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-cyan-400/30 ${isOpen ? 'rotate-3 border-cyan-300 bg-cyan-600 shadow-cyan-900/60' : 'border-cyan-500/70 bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-700 shadow-blue-950/60'}`}
        aria-label={check?.id
          ? `${isOpen ? 'Collapse' : 'Open'} incident readiness: ${readinessPercentage}% ready (${counts.ready} of ${readinessRecipientCount})`
          : isOpen ? 'Collapse incident readiness' : 'Open incident readiness'}
        aria-controls="incident-readiness-panel"
        aria-expanded={isOpen}
        title={check?.id ? `Incident readiness: ${readinessPercentage}% ready` : 'Incident readiness'}
        data-testid="incident-readiness-launcher"
      >
        <span className="absolute inset-1 rounded-full border border-white/15" />
        {check?.id ? (
          <span className={`relative text-[13px] font-black tabular-nums transition-transform duration-200 ${isOpen ? 'scale-90' : 'group-hover:scale-110'}`} data-testid="incident-readiness-percentage">
            {readinessPercentage}%
          </span>
        ) : (
          <Radio className={`relative h-7 w-7 transition-transform duration-200 ${isOpen ? 'scale-90' : 'group-hover:scale-110'}`} />
        )}
        <span className={`absolute right-0 top-0 h-4 w-4 rounded-full border-[3px] border-slate-950 ${checkIsActive ? counts.pending > 0 ? 'animate-pulse bg-amber-400' : 'bg-emerald-400' : 'bg-slate-400'}`} />
      </button>
    </>,
    document.body,
  );
}
